import { PlusIcon, SearchIcon } from "@/design/icons";
import { type FC, useMemo, useState } from "react";

import { Button } from "@/design/ui/button";
import { Input } from "@/design/ui/input";
import { Skeleton } from "@/design/ui/skeleton";
import { GroupLabel, Row } from "@/design/Row";
import { cn } from "@/agents-v2/utils/cn";

import type { ITenderBid } from "../../types";
import { AccountMenu } from "./WorkspaceHeader";
import { SessionExpiredBanner } from "../components/SessionExpiredBanner";

const RECENT = 5;

export type HomeScreenProps = {
  tenders: ITenderBid[];
  loading: boolean;
  error: string | null;
  sessionExpired: boolean;
  suggested: ITenderBid | null;
  email: string | null;
  /** Shown when the document is already linked and the user came to switch. */
  currentBidId?: string | null;
  onLink: (bidId: string) => void;
  onCreate: () => void;
  onSkip: () => void;
  onRetry: () => void;
  onSignOut: () => void;
  onOpenMytender: () => void;
  onDismissSessionExpired: () => void;
};

const deadlineText = (bid: ITenderBid): string | null => {
  const raw = (bid as ITenderBid & { submission_deadline?: string }).submission_deadline;
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return `Due ${date.toLocaleDateString(undefined, { day: "numeric", month: "short" })}`;
};

const meta = (bid: ITenderBid): string =>
  [bid.creation_flow === "agent" ? "Setup in progress" : null, bid.client_name, deadlineText(bid)]
    .filter(Boolean)
    .join(" · ");

/**
 * Home: which tender is this document for? Shown whenever the document is
 * not linked. One suggestion from the document's title, the recent tenders,
 * then everything; Create and "continue without" at the bottom.
 */
export const HomeScreen: FC<HomeScreenProps> = ({
  tenders,
  loading,
  error,
  sessionExpired,
  suggested,
  email,
  currentBidId,
  onLink,
  onCreate,
  onSkip,
  onRetry,
  onSignOut,
  onOpenMytender,
  onDismissSessionExpired,
}) => {
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);
  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      q
        ? tenders.filter((b) => `${b.bid_title} ${b.client_name ?? ""}`.toLowerCase().includes(q))
        : tenders.filter((b) => b._id !== suggested?._id),
    [tenders, q, suggested]
  );
  const visible = q || showAll ? filtered : filtered.slice(0, RECENT);
  const hidden = filtered.length - visible.length;

  return (
    <div className="flex h-full flex-col bg-surface">
      <div className="flex h-11 shrink-0 items-center justify-between border-b border-line px-3">
        <div className="flex items-center gap-2">
          <span className="flex size-[22px] items-center justify-center rounded-sm bg-fg text-xs font-bold text-fg-inverse">
            io
          </span>
          <span className="text-base font-semibold">io.ai</span>
        </div>
        <AccountMenu email={email} onSignOut={onSignOut} onOpenMytender={onOpenMytender} />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {sessionExpired && <SessionExpiredBanner onDismiss={onDismissSessionExpired} onSignOut={onSignOut} />}
        <div className="px-4 pb-3 pt-5">
          <h1 className="text-lg font-semibold">
            {currentBidId ? "Switch tender" : "Which tender is this document for?"}
          </h1>
          <p className="mt-1 text-sm text-fg-2">io.ai will use its documents, criteria and storyboard.</p>
        </div>
        <div className="px-3 pb-2">
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-fg-3" />
            <Input
              type="search"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tenders"
              aria-label="Search tenders"
              className="h-[34px] bg-subtle pl-8"
            />
          </div>
        </div>

        {loading && tenders.length === 0 ? (
          <div className="flex flex-col gap-2 px-3 pt-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex flex-col gap-1.5 py-1.5">
                <Skeleton className="h-3.5 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        ) : error && tenders.length === 0 && !sessionExpired ? (
          <div className="px-3 pt-4">
            <div className="rounded-md border border-line bg-subtle p-3 text-sm">
              <div className="font-medium">Couldn&apos;t load your tenders</div>
              <div className="mt-0.5 text-fg-2" title={error}>
                Check your connection, then try again.
              </div>
              <Button variant="outline" size="sm" className="mt-2" onClick={onRetry}>
                Retry
              </Button>
            </div>
          </div>
        ) : (
          <>
            {suggested && !q && suggested._id !== currentBidId && (
              <>
                <GroupLabel>Suggested</GroupLabel>
                <Row
                  first
                  title={
                    <span className="inline-flex items-center gap-1.5">
                      <span className="size-1.5 rounded-full bg-accent" />
                      {suggested.bid_title}
                    </span>
                  }
                  meta={`Matches your document title${meta(suggested) ? ` · ${meta(suggested)}` : ""}`}
                  trailing={
                    <Button size="sm" className="pointer-events-none">
                      Link
                    </Button>
                  }
                  trailingAlways
                  onClick={() => onLink(suggested._id)}
                />
              </>
            )}
            {visible.length > 0 && <GroupLabel>{q ? "Results" : showAll ? "All tenders" : "Recent"}</GroupLabel>}
            {visible.map((bid, i) => (
              <Row
                key={bid._id}
                first={i === 0}
                title={bid.bid_title}
                meta={meta(bid) || " "}
                selected={bid._id === currentBidId}
                trailing={
                  <span className="text-sm font-semibold text-accent">
                    {bid.creation_flow === "agent" ? "Continue" : "Link"}
                  </span>
                }
                onClick={() => onLink(bid._id)}
              />
            ))}
            {!q && hidden > 0 && (
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="w-full border-t border-line/70 px-3 py-2.5 text-left text-sm text-fg-2 hover:bg-subtle"
              >
                Show all {tenders.length} tenders
              </button>
            )}
            {q && visible.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-fg-2">No tenders match “{query}”.</div>
            )}
            {!q && tenders.length === 0 && !loading && !sessionExpired && (
              <div className="px-3 py-6 text-center text-sm text-fg-2">No tenders yet. Create one to get started.</div>
            )}
          </>
        )}

        <div className="p-3">
          <button
            type="button"
            onClick={onCreate}
            className={cn(
              "flex w-full items-center gap-2.5 rounded-md border border-dashed border-line-strong bg-surface p-3 text-left",
              "transition-colors duration-fast hover:bg-subtle"
            )}
          >
            <span className="flex size-7 items-center justify-center rounded-sm bg-accent-soft text-accent-hover">
              <PlusIcon className="size-3.5" />
            </span>
            <span>
              <span className="block font-semibold">Create a tender</span>
              <span className="block text-sm text-fg-2">Drop the tender pack and io.ai sets it up</span>
            </span>
          </button>
        </div>
      </div>

      <div className="shrink-0 border-t border-line/70 px-4 py-2.5 text-center">
        <button type="button" onClick={onSkip} className="text-sm text-fg-2 hover:text-fg">
          {currentBidId ? "Keep current tender" : "Continue without a tender"}
        </button>
      </div>
    </div>
  );
};
